import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import AppConst from '../apis/appconst';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Select from 'react-select';

function Daybook() {
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totals, setTotals] = useState({ cash: 0, card: 0, online: 0, total: 0 });
    const [departmentTotals, setDepartmentTotals] = useState({});

    const [returnTotals, setReturnTotals] = useState({
        pharmacy_returns: 0, ipd_returns: 0, total_returns: 0, net_collection: 0
    });
    const [users, setUsers] = useState([]);

    const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedPaymentModes, setSelectedPaymentModes] = useState([]);   // ← multiselect
    const [selectedUsers, setSelectedUsers] = useState([]);                  // ← multiselect
    const [searchKey, setSearchKey] = useState('');

    // Department options
    const departmentOptions = [
        { value: 'ipd_reg', label: 'IPD Registration' }, 
        { value: 'ipd', label: 'IPD Billing' },
        { value: 'ipd_advance', label: 'IPD Advance' },
        { value: 'opd', label: 'OPD' },
        { value: 'opd_procedure', label: 'OPD Procedure' },
        { value: 'emergency', label: 'Emergency' },
        { value: 'lab', label: 'Lab' },
        { value: 'pharmacy', label: 'Pharmacy' },
        
       
    ];

    // Payment Mode options
    const paymentModeOptions = [
        { value: 'Cash', label: 'Cash' },
        { value: 'Card', label: 'Card' },
        { value: 'Online', label: 'Online' },
    ];

    // User options derived from API response
    const userOptions = useMemo(() => {
        return users.map(u => ({
            value: u.user_id,
            label: u.fname + (u.lname ? ' ' + u.lname : '')
        }));
    }, [users]);

    // react-select styles to match Bootstrap sm
    const reactSelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '31px',
            fontSize: '13px',
            borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
            boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
            '&:hover': { borderColor: '#86b7fe' },
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 6px',
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: '#e7f1ff',
            borderRadius: '3px',
            fontSize: '12px',
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: '#0d6efd',
            padding: '1px 4px',
            fontSize: '12px',
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: '#0d6efd',
            ':hover': { backgroundColor: '#0d6efd', color: 'white' },
        }),
        placeholder: (base) => ({
            ...base,
            color: '#6c757d',
            fontSize: '13px',
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999,
            fontSize: '13px',
        }),
        option: (base, state) => ({
            ...base,
            fontSize: '13px',
            padding: '6px 10px',
            backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e7f1ff' : 'white',
            color: state.isSelected ? 'white' : '#212529',
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: '31px',
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: '4px',
        }),
        clearIndicator: (base) => ({
            ...base,
            padding: '4px',
        }),
    };

    const fetchTransactions = useCallback(async () => {
        setIsLoading(true);
        try {
            const fd = new FormData();
            fd.append('from_date', fromDate);
            fd.append('to_date', toDate);

            // Department: comma-separated or 'all'
            if (selectedDepartments.length === 0) {
                fd.append('department', 'all');
            } else {
                fd.append('department', selectedDepartments.map(d => d.value).join(','));
            }

            // Payment Mode: comma-separated or 'all'
            if (selectedPaymentModes.length === 0) {
                fd.append('payment_mode', 'all');
            } else {
                fd.append('payment_mode', selectedPaymentModes.map(m => m.value).join(','));
            }

            // User: comma-separated or 'all'
            if (selectedUsers.length === 0) {
                fd.append('user_id', 'all');
            } else {
                fd.append('user_id', selectedUsers.map(u => u.value).join(','));
            }

            const response = await axios.post(
                AppConst.BASE_URL + AppConst.getDaybookTransactionsUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response?.data?.status) {
                setTransactions(response.data.transactions || []);
                setTotals(response.data.totals || { cash: 0, card: 0, online: 0, total: 0 });
                setDepartmentTotals(response.data.department_totals || {});
                setUsers(response.data.users || []);
                setReturnTotals(response.data.return_totals || {
                    pharmacy_returns: 0, ipd_returns: 0, total_returns: 0, net_collection: 0
                });
            } else {
                toast.error(response?.data?.message || 'Failed to fetch transactions');
                setTransactions([]);
            }
        } catch (error) {
            console.error('Fetch daybook error:', error);
            toast.error('Failed to fetch transactions');
            setTransactions([]);
        } finally {
            setIsLoading(false);
        }
    }, [fromDate, toDate, selectedDepartments, selectedPaymentModes, selectedUsers]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    const handleSearch = () => {
        fetchTransactions();
    };

    const handleClearSearch = () => {
        setSearchKey('');
    };

    // Client-side text search only — no department/mode/user filtering here
    const filteredTransactions = useMemo(() => {
        if (!searchKey.trim()) return transactions;
        const search = searchKey.toLowerCase();
        return transactions.filter(trx =>
            (trx.uhid || '').toLowerCase().includes(search) ||
            (trx.patient_name || '').toLowerCase().includes(search) ||
            (trx.received_by_name || '').toLowerCase().includes(search) ||
            (trx.department || '').toLowerCase().includes(search) ||
            (trx.transaction_id || '').toLowerCase().includes(search)
        );
    }, [transactions, searchKey]);

    // Use backend totals when no text search active
    const filteredTotals = useMemo(() => {
        if (!searchKey.trim()) return totals;
        return filteredTransactions.reduce((acc, trx) => {
            const amount = Number(trx.payment_amount || 0);
            acc.total += amount;
            if (typeof trx.cash !== 'undefined' || typeof trx.card !== 'undefined' || typeof trx.online !== 'undefined') {
                acc.cash += Number(trx.cash || 0);
                acc.card += Number(trx.card || 0);
                acc.online += Number(trx.online || 0);
                return acc;
            }
            const mode = (trx.payment_mode || '').toLowerCase();
            if (mode.includes('cash')) acc.cash += amount;
            else if (mode.includes('card')) acc.card += amount;
            else if (mode.includes('online') || mode.includes('upi')) acc.online += amount;
            else acc.cash += amount;
            return acc;
        }, { cash: 0, card: 0, online: 0, total: 0 });
    }, [filteredTransactions, searchKey, totals]);

    // Use backend department totals when no text search active
    const filteredDepartmentTotals = useMemo(() => {
        if (!searchKey.trim()) return departmentTotals;
        return filteredTransactions.reduce((acc, trx) => {
            const dept = trx.dept_key || '';
            const amount = Number(trx.payment_amount || 0);
            if (dept && amount > 0) {
                acc[dept] = (acc[dept] || 0) + amount;
            }
            return acc;
        }, {});
    }, [filteredTransactions, searchKey, departmentTotals]);

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const date = new Date(dateStr);
        if (isNaN(date)) return dateStr;
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return '';
        try {
            const parts = timeStr.split(':');
            if (parts.length < 2) return timeStr;
            const h = parseInt(parts[0]);
            const m = parts[1];
            const ampm = h >= 12 ? 'PM' : 'AM';
            return `${h % 12 || 12}:${m} ${ampm}`;
        } catch { return timeStr; }
    };

    const getDeptBadgeClass = (dept) => {
        const classes = {
            'IPD Billing': 'bg-primary',
            'IPD Advance': 'bg-info',
            'IPD Refund': 'bg-danger',
            'OPD': 'bg-success',
            'OPD Procedure': 'bg-success',
            'Emergency': 'bg-danger',
            'Lab': 'bg-warning text-dark',
            'Lab Payment': 'bg-warning text-dark',
            'Pharmacy': 'bg-secondary',
            'Pharmacy Payment': 'bg-secondary',
            'Pharmacy Return': 'bg-danger'
        };
        return classes[dept] || 'bg-dark';
    };

    const getPaymentModeBadge = (mode) => {
        const m = (mode || '').toLowerCase();
        if (m.includes('cash')) return <span className="badge bg-success">Cash</span>;
        if (m.includes('card')) return <span className="badge bg-primary">Card</span>;
        if (m.includes('online') || m.includes('upi')) return <span className="badge bg-info">Online</span>;
        return <span className="badge bg-secondary">{mode || 'Cash'}</span>;
    };

    const handlePrintPDF = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('DAYBOOK REPORT', pageWidth / 2, 15, { align: 'center' });

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Date: ${formatDate(fromDate)} to ${formatDate(toDate)}`, pageWidth / 2, 22, { align: 'center' });

        const tableData = filteredTransactions.map((trx, idx) => [
            idx + 1,
            trx.uhid || 'N/A',
            (trx.patient_name || 'N/A').substring(0, 18),
            trx.department || '',
            formatDate(trx.payment_date),
            formatTime(trx.payment_time),
            trx.payment_mode || 'Cash',
            `₹${Number(trx.payment_amount || 0).toFixed(2)}`,
            (trx.received_by_name || 'N/A').substring(0, 12)
        ]);

        autoTable(doc, {
            startY: 30,
            head: [['#', 'UHID', 'Patient', 'Dept', 'Date', 'Time', 'Mode', 'Amount', 'Received']],
            body: tableData,
            theme: 'grid',
            headStyles: { fillColor: [64, 81, 137], fontSize: 8 },
            styles: { fontSize: 7, cellPadding: 1.5 }
        });

        let y = doc.lastAutoTable.finalY + 8;

        doc.setFont('helvetica', 'bold');
        doc.text('SUMMARY', 14, y);
        y += 5;

        autoTable(doc, {
            startY: y,
            body: [
                ['Cash', `₹${filteredTotals.cash.toFixed(2)}`],
                ['Card', `₹${filteredTotals.card.toFixed(2)}`],
                ['Online', `₹${filteredTotals.online.toFixed(2)}`],
                ['TOTAL', `₹${filteredTotals.total.toFixed(2)}`]
            ],
            theme: 'grid',
            styles: { fontSize: 10 },
            columnStyles: { 1: { halign: 'right' } },
            margin: { left: 14 }
        });

        const pdfBlob = doc.output('blob');
window.open(URL.createObjectURL(pdfBlob), '_blank');  // ✅ works in jsPDF v3
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 style={{ color: '#405189' }}>
                        <i className="ri-book-2-line me-2"></i>Daybook
                    </h4>
                    <button className="btn btn-primary btn-sm" onClick={handlePrintPDF}>
                        <i className="ri-printer-line me-1"></i> Print
                    </button>
                </div>

                {/* Filters */}
                <div className="card mb-3">
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-lg-2 col-md-4 col-6">
                                <label className="form-label small mb-1">From</label>
                                <input type="date" className="form-control form-control-sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                            </div>
                            <div className="col-lg-2 col-md-4 col-6">
                                <label className="form-label small mb-1">To</label>
                                <input type="date" className="form-control form-control-sm" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                            </div>

                            {/* Department — multiselect */}
                            <div className="col-lg-2 col-md-4 col-6">
                                <label className="form-label small mb-1">Department</label>
                                <Select
                                    isMulti
                                    options={departmentOptions}
                                    value={selectedDepartments}
                                    onChange={(selected) => setSelectedDepartments(selected || [])}
                                    placeholder="All Departments"
                                    isClearable
                                    styles={reactSelectStyles}
                                    closeMenuOnSelect={false}
                                />
                            </div>

                            {/* Payment Mode — multiselect */}
                            <div className="col-lg-2 col-md-4 col-6">
                                <label className="form-label small mb-1">Mode</label>
                                <Select
                                    isMulti
                                    options={paymentModeOptions}
                                    value={selectedPaymentModes}
                                    onChange={(selected) => setSelectedPaymentModes(selected || [])}
                                    placeholder="All Modes"
                                    isClearable
                                    styles={reactSelectStyles}
                                    closeMenuOnSelect={false}
                                />
                            </div>

                            {/* User — multiselect */}
                            <div className="col-lg-3 col-md-4 col-6">
                                <label className="form-label small mb-1">User</label>
                                <Select
                                    isMulti
                                    options={userOptions}
                                    value={selectedUsers}
                                    onChange={(selected) => setSelectedUsers(selected || [])}
                                    placeholder="All Users"
                                    isClearable
                                    isSearchable
                                    styles={reactSelectStyles}
                                    closeMenuOnSelect={false}
                                />
                            </div>

                            <div className="col-lg-1 col-md-4 col-6">
                                <button className="btn btn-primary btn-sm w-100" onClick={handleSearch} disabled={isLoading}>
                                    {isLoading ? <span className="spinner-border spinner-border-sm"></span> : <><i className="ri-search-line me-1"></i>Search</>}
                                </button>
                            </div>
                        </div>
                        
                        {/* Search Box */}
                        <div className="row mt-3">
                            <div className="col-md-6">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text bg-light">
                                        <i className="ri-search-line"></i>
                                    </span>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        placeholder="🔍 Search by UHID, Patient Name, Department..." 
                                        value={searchKey} 
                                        onChange={(e) => setSearchKey(e.target.value)} 
                                    />
                                    {searchKey && (
                                        <button 
                                            className="btn btn-outline-secondary" 
                                            type="button"
                                            onClick={handleClearSearch}
                                        >
                                            <i className="ri-close-line"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-6 d-flex align-items-center">
                                <small className="text-muted">
                                    {searchKey || selectedDepartments.length > 0 || selectedPaymentModes.length > 0 || selectedUsers.length > 0 ? (
                                        <>Showing <strong>{filteredTransactions.length}</strong> of <strong>{transactions.length}</strong> transactions</>
                                    ) : (
                                        <><strong>{filteredTransactions.length}</strong> transactions</>
                                    )}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="row mb-3 g-2">
                    <div className="col-md-3 col-6">
                        <div className="card border-success h-100">
                            <div className="card-body text-center py-2">
                                <small className="text-muted">Cash</small>
                                <h5 className="text-success mb-0">₹{filteredTotals.cash.toFixed(2)}</h5>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 col-6">
                        <div className="card border-primary h-100">
                            <div className="card-body text-center py-2">
                                <small className="text-muted">Card</small>
                                <h5 className="text-primary mb-0">₹{filteredTotals.card.toFixed(2)}</h5>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 col-6">
                        <div className="card border-info h-100">
                            <div className="card-body text-center py-2">
                                <small className="text-muted">Online</small>
                                <h5 className="text-info mb-0">₹{filteredTotals.online.toFixed(2)}</h5>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 col-6">
                        <div className="card bg-primary text-white h-100">
                            <div className="card-body text-center py-2">
                                <small className="text-white-50">Total</small>
                                <h5 className="text-white mb-0">₹{filteredTotals.total.toFixed(2)}</h5>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Department Summary */}
                <div className="card mb-3">
                    <div className="card-body py-2">
                        <div className="d-flex flex-wrap justify-content-around text-center gap-2">
                        {[

                                { key: 'ipd_reg', label: 'IPD Reg', color: 'primary' }, 
                                { key: 'ipd', label: 'IPD Bill', color: 'primary' },
                                { key: 'ipd_advance', label: 'IPD Adv', color: 'info' },
                                { key: 'opd', label: 'OPD', color: 'success' },
                                { key: 'opd_procedure', label: 'OPD Proc', color: 'success' },
                                { key: 'emergency', label: 'Emergency', color: 'danger' },
                                { key: 'lab', label: 'Lab', color: 'warning' },
                                { key: 'pharmacy', label: 'Pharmacy', color: 'secondary' },
                                { key: 'pharmacy_return', label: 'Pharma Ret', color: 'danger', isNegative: true },
                                { key: 'ipd_return', label: 'IPD Refund', color: 'danger', isNegative: true }
                            ].map(d => (
                                <div key={d.key}>
                                    <span className={`badge bg-${d.color} ${d.color === 'warning' ? 'text-dark' : ''}`} style={{ fontSize: '10px' }}>{d.label}</span>
                                    <div className={`small fw-bold ${d.isNegative ? 'text-danger' : ''}`}>
                                        {d.isNegative ? '- ' : ''}₹{Math.abs(filteredDepartmentTotals[d.key] || 0).toFixed(2)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Transactions Table */}
                <div className="card">
                    <div className="card-body p-0">
                        <div className="table-responsive" style={{ maxHeight: '400px' }}>
                            <table className="table table-sm table-hover table-striped mb-0" style={{ fontSize: '11px' }}>
                                <thead className="table-dark sticky-top">
                                    <tr>
                                        <th>#</th>
                                        <th>UHID</th>
                                        <th>Patient</th>
                                        <th>Dept</th>
                                        <th>Date</th>
                                        <th>Time</th>
                                        <th>Mode</th>
                                        <th className="text-end">Amount</th>
                                        <th>Received By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr><td colSpan="9" className="text-center py-3"><div className="spinner-border spinner-border-sm"></div></td></tr>
                                    ) : filteredTransactions.length === 0 ? (
                                        <tr><td colSpan="9" className="text-center py-3 text-muted">
                                            {searchKey ? `No transactions found for "${searchKey}"` : 'No transactions'}
                                        </td></tr>
                                    ) : (
                                        filteredTransactions.map((trx, i) => (
                                            <tr key={trx.transaction_id || i} 
                                                style={trx.is_return || Number(trx.payment_amount) < 0 ? { backgroundColor: '#fff5f5' } : {}}>
                                                <td>{i + 1}</td>
                                                <td><strong className="text-primary">{trx.uhid || 'N/A'}</strong></td>
                                                <td>{trx.patient_name || 'N/A'}</td>
                                                <td>
                                                    <span className={`badge ${getDeptBadgeClass(trx.department)}`} style={{ fontSize: '9px' }}>
                                                        {trx.department}
                                                    </span>
                                                </td>
                                                <td>{formatDate(trx.payment_date)}</td>
                                                <td>{formatTime(trx.payment_time)}</td>
                                                <td>{getPaymentModeBadge(trx.payment_mode)}</td>
                                                <td className={`text-end fw-bold ${Number(trx.payment_amount) < 0 ? 'text-danger' : 'text-success'}`}>
                                                    {Number(trx.payment_amount) < 0 ? '- ' : ''}₹{Math.abs(Number(trx.payment_amount || 0)).toFixed(2)}
                                                </td>
                                                <td>{trx.received_by_name || 'N/A'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                                {filteredTransactions.length > 0 && (
                                    <tfoot className="table-light">
                                        <tr><td colSpan="7" className="text-end fw-bold">Total:</td><td className="text-end text-primary fw-bold">₹{filteredTotals.total.toFixed(2)}</td><td></td></tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                </div>


                {/* Returns & Net Collection */}
                {(returnTotals.pharmacy_returns > 0 || returnTotals.ipd_returns > 0) && (
                    <div className="row mb-3 g-2">
                        {returnTotals.pharmacy_returns > 0 && (
                            <div className="col-md-3 col-6">
                                <div className="card border-danger h-100">
                                    <div className="card-body text-center py-2">
                                        <small className="text-muted">Pharmacy Returns</small>
                                        <h6 className="text-danger mb-0">- ₹{returnTotals.pharmacy_returns.toFixed(2)}</h6>
                                    </div>
                                </div>
                            </div>
                        )}
                        {returnTotals.ipd_returns > 0 && (
                            <div className="col-md-3 col-6">
                                <div className="card border-danger h-100">
                                    <div className="card-body text-center py-2">
                                        <small className="text-muted">IPD Refunds</small>
                                        <h6 className="text-danger mb-0">- ₹{returnTotals.ipd_returns.toFixed(2)}</h6>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="col-md-3 col-6">
                            <div className="card bg-success text-white h-100">
                                <div className="card-body text-center py-2">
                                    <small className="text-white-50">Net Collection</small>
                                    <h5 className="text-white mb-0">₹{filteredTotals.total.toFixed(2)}</h5>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Daybook;